<script setup lang="ts">
import { computed, useAttrs } from 'vue';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';
import { convertToTitleCase } from '@Helpers';

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();

const enhancedOptions = computed(() => {
  if (!attrs.options || !Array.isArray(attrs.options)) return [];
  return attrs.options?.map((option: any) => ({
    ...option,
    _originalName: option.name,
    name: `${option.name} ${option.value || ''}`,
  }));
});
</script>

<template>
  <FloatLabel
    v-tooltip.left="attrs.hint ? $t(attrs.hint as string) : ''"
    class="mt-2 w-full"
  >
    <MultiSelect
      v-bind="{ ...attrs, FILTER_MESSAGES, EMPTY_MESSAGES, options: enhancedOptions }"
      :selected="true"
      display="chip"
      pt:root="w-full"
      pt:emptyMessage="text-xs"
      pt:filterInput="h-9 py-1 pl-3 text-xs"
      pt:headerCheckbox:box="size-5"
      pt:itemCheckbox:box="size-5"
      pt:labelContainer="h-9 p-1 pr-7 text-xs flex items-center"
      pt:panel="sm:w-[265px] md:w-[330px] lg:w-[268px] "
      pt:token="rounded text-sm py-1 px-2"
      pt:option="text-sm"
    >
      <template #value="slotProps">
        <template v-if="slotProps.value.length">
          <span
            v-for="(option, index) in slotProps.value"
            class="mr-1 rounded-md bg-brand-100 px-2 py-1 font-medium text-gray-600"
            :key="index"
          >
            {{ convertToTitleCase($t(option._originalName || option.name)) }}
          </span>
        </template>

        <span
          v-else
          class="w-[90%] overflow-hidden text-ellipsis whitespace-nowrap"
        >
          {{ $t(attrs.placeholder as string) }}
        </span>
      </template>

      <template #option="slotProps">
        <span class="text-xs">
          {{ convertToTitleCase($t(slotProps.option._originalName || slotProps.option.name)) }}
        </span>
      </template>

      <slot />
    </MultiSelect>

    <label
      v-if="attrs.label"
      class="-mt-2.5 flex w-[90%] gap-2 overflow-hidden text-ellipsis whitespace-nowrap text-sm text-zinc-500"
    >
      <div class="hidden sm:block">
        <FaIcon
          v-if="attrs.hint"
          aria-hidden
          class="size-3 text-gray-500"
          :icon="$icons.faCircleInfo"
        />
      </div>

      <span class="w-[90%] overflow-hidden text-ellipsis whitespace-nowrap">
        {{ $t(attrs.label as string) }}
      </span>
    </label>
  </FloatLabel>
</template>
